import { useMemo, useState } from 'react';
import type { ChampionSummary } from '../types';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { Draggable } from '../dnd';
import ChampionIcon from './ChampionIcon';

interface Props {
  champions: ChampionSummary[];
  roles: string[];
  /** Champion ids already used somewhere in the draft (dimmed + not clickable). */
  usedIds: Set<string>;
  onPick: (c: ChampionSummary) => void;
}

/** Searchable, role-filterable, draggable grid of champion icons. */
export default function ChampionGrid({ champions, roles, usedIds, onPick }: Props) {
  const [search, setSearch] = useState('');
  const [role, setRole] = useState<string | null>(null);
  const debouncedSearch = useDebouncedValue(search, 200);

  const filtered = useMemo(() => {
    const needle = debouncedSearch.trim().toLowerCase();
    return champions.filter(
      (c) =>
        (!needle || c.name.toLowerCase().includes(needle)) &&
        (!role || c.roles.includes(role)),
    );
  }, [champions, debouncedSearch, role]);

  return (
    <section className="grid-panel">
      <div className="grid-controls">
        <input
          className="search"
          type="search"
          placeholder="Search champions…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="role-chips">
          <button
            type="button"
            className={`chip${role === null ? ' chip--active' : ''}`}
            onClick={() => setRole(null)}
          >
            All
          </button>
          {roles.map((r) => (
            <button
              key={r}
              type="button"
              className={`chip${role === r ? ' chip--active' : ''}`}
              onClick={() => setRole((cur) => (cur === r ? null : r))}
            >
              {r}
            </button>
          ))}
        </div>
        <span className="grid-count">{filtered.length} champions</span>
      </div>

      <div className="champ-grid">
        {filtered.map((c) => {
          const used = usedIds.has(c.id);
          return (
            <Draggable key={c.id} id={`grid:${c.id}`} data={{ championId: c.id, from: 'grid' }} disabled={used}>
              <ChampionIcon champion={c} size={56} disabled={used} onClick={onPick} />
            </Draggable>
          );
        })}
      </div>
    </section>
  );
}
